import { Component, ChangeDetectionStrategy, computed, input } from '@angular/core';
import { Bucket, BucketProgress } from '../../types';

type ProgressColor = NonNullable<BucketProgress['color']>;

@Component({
    selector: 'app-bucket-progress',
    templateUrl: './bucket-progress.component.html',
    styleUrl: './bucket-progress.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BucketProgressComponent {
  bucket = input.required<Bucket>();
  progress = computed(() => this.getProgress(this.bucket()));
  readonly ringCircumference = 289.027;

  constructor() {}

  /**
   * Every ring starts empty and fills; red always means money spent that is not there.
   * - Savings with a goal: balance toward the goal.
   * - Spending, and savings without a goal (a sinking fund): this month's spending
   *   against everything available, so the ring is full exactly when available hits $0.
   * - Income: received against this month's expected amount. Never red — a short
   *   month can't be judged before payday.
   */
  getProgress(bucket: Bucket): BucketProgress {
    const balance = bucket.fund?.balance ?? 0;
    const goal = bucket.fund?.goal ?? 0;

    switch (bucket.type) {
      case 'INCOME':
        return incomeProgress(bucket.total, bucket.amount);
      case 'SAVINGS':
        return goal > 0 ? savingsGoalProgress(balance, goal) : spendingProgress(balance, bucket.total);
      case 'BUDGET':
        return spendingProgress(balance, bucket.total);
      default:
        return { percent: 0, color: 'primary' };
    }
  }

  get progressOffset(): number {
    return this.ringCircumference * (1 - this.progressPercent / 100);
  }

  get progressPercent(): number {
    const percent = this.progress().percent;
    return Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  }

  get roundedProgressPercent(): number {
    return Math.round(this.progressPercent);
  }

  get progressColor(): ProgressColor {
    return this.progress().color || 'primary';
  }
}

function savingsGoalProgress(balance: number, goal: number): BucketProgress {
  if (balance < 0) return { percent: 100, color: 'warn' };
  return { percent: (balance / goal) * 100, color: 'primary' };
}

function spendingProgress(balance: number, total: number): BucketProgress {
  if (balance < 0) return { percent: 100, color: 'warn' };
  const spent = Math.max(0, -total);
  const startingAvailable = balance + spent;
  return { percent: startingAvailable > 0 ? (spent / startingAvailable) * 100 : 0, color: 'primary' };
}

function incomeProgress(received: number, expected: number): BucketProgress {
  if (expected > 0) return { percent: (received / expected) * 100, color: 'primary' };
  return { percent: received > 0 ? 100 : 0, color: 'primary' };
}
